// The reporter's telephone.
//
// Everything else this SDK draws is a consequence of something the person on
// the page did: they pressed the widget, they filed a report, they asked to
// talk it through. This is the one thing that arrives unasked — a member of the
// team has a question about a report and is ringing to ask it — and that makes
// it the piece with the most rules around it.
//
// # What it is allowed to do
//
// Appear, and make a noise. That is the whole of it. No microphone is opened,
// no audio moves and nothing is connected until the person presses Answer, and
// if they press nothing at all it takes itself away when the ringing stops. The
// card is an offer; the SDK does not put a customer on a call because somebody
// at the vendor clicked a button.
//
// # And what it says
//
// A name, a product and what the call is about, because a card that says
// "someone is calling about a report" is indistinguishable from the thing every
// bank tells people to hang up on. And whether the call would be recorded,
// stated before Answer rather than after — on a peer-to-peer call the copy that
// gets kept is uplinked by this browser, so telling somebody afterwards is
// telling them about something that already happened.
//
// # The noise
//
// Synthesised, for the reason the dashboard's is: a ringtone as an audio file
// is a binary in a bundle that runs on somebody else's site, an extra request
// on their page, and a thing that can go missing behind a cache header. Four
// oscillators and a table of numbers cost none of that.
//
// It is also softer and shorter than the dashboard's. A phone on a support
// desk is meant to interrupt; this one is ringing in the middle of a stranger's
// shopping cart, and a page that suddenly plays a loud telephone at somebody is
// a page they close. If the browser refuses to play it at all — no interaction
// yet, which is its rule and not ours — the card rings silently rather than not
// appearing.

/**
 * A quiet double-pulse, once every three seconds.
 *
 * Two notes rather than one so it reads as a telephone rather than as an alert,
 * and a fifth apart because that interval is the one people hear as "ringing"
 * across every phone system anybody has used.
 */
const RING = {
  cycleMs: 3000,
  peak: 0.045,
  bursts: [
    { at: 0, seconds: 0.4, hz: [440, 660] },
    { at: 0.55, seconds: 0.4, hz: [440, 660] },
  ],
};

/**
 * Whether anybody has touched this page yet.
 *
 * Tracked rather than asked, because there is nothing to ask: a browser will
 * tell you an AudioContext is suspended, but only after you have built one —
 * and building one on a page with no interaction is what writes
 *
 *     The AudioContext was not allowed to start. It must be resumed (or
 *     created) after a user gesture on the page.
 *
 * into the console. This SDK runs on somebody else's site, and that console
 * belongs to whoever is trying to debug their own product. A warning we cannot
 * act on, for a sound that was never going to play, is not ours to put there.
 */
let touched = false;
if (typeof window !== 'undefined' && window.addEventListener) {
  for (const evt of ['pointerdown', 'keydown', 'touchstart']) {
    // Capture, and it matters more here than it would in our own app: this runs
    // on somebody else's page, whose handlers we have never seen and one of
    // which may well stop a click before it reaches the window.
    window.addEventListener(evt, () => { touched = true; }, {
      once: true, capture: true, passive: true,
    });
  }
}

/**
 * The same question asked of the browser rather than of ourselves.
 *
 * Chrome and Edge implement this and are the browsers that enforce the policy
 * in the first place, so where it matters it answers. It is the recovery for a
 * host page that swallowed every gesture before we saw it — better to make a
 * quiet noise the person expects than to stay silent because of somebody else's
 * event handler.
 */
function activatedByBrowser() {
  try {
    return Boolean(navigator.userActivation?.hasBeenActive);
  } catch {
    return false;
  }
}

/**
 * Start ringing, and give back the way to stop.
 *
 * Returns a no-op teardown on every browser that will not do it, which is a
 * real and ordinary case: a page nobody has clicked on yet has no audio
 * permission, and the correct behaviour there is a card that appears in silence
 * rather than no card.
 */
function startRinging() {
  if (!touched && !activatedByBrowser()) return () => {};

  const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!Ctx) return () => {};

  let context;
  try {
    context = new Ctx();
  } catch {
    return () => {};
  }

  let stopped = false;
  let timer = null;
  const voices = [];

  const burst = ({ at, seconds, hz }) => {
    const start = context.currentTime + at;
    const gain = context.createGain();
    gain.connect(context.destination);
    // Shaped rather than switched. A square edge on a sine is a click, and a
    // click every three seconds is more irritating than the tone it introduces.
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(RING.peak, start + 0.02);
    gain.gain.setValueAtTime(RING.peak, start + seconds - 0.06);
    gain.gain.linearRampToValueAtTime(0, start + seconds);

    for (const frequency of hz) {
      const osc = context.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      osc.connect(gain);
      osc.start(start);
      osc.stop(start + seconds + 0.02);
      voices.push(osc);
    }
  };

  const cycle = () => {
    if (stopped) return;
    try {
      RING.bursts.forEach(burst);
    } catch { /* the card is still on screen, which is the part that matters */ }
  };

  const begin = () => {
    if (stopped) return;
    cycle();
    timer = setInterval(cycle, RING.cycleMs);
  };

  if (context.state === 'running') begin();
  else context.resume().then(begin).catch(() => {});

  return () => {
    stopped = true;
    if (timer) clearInterval(timer);
    // Stopped rather than left to finish. Answered, this is cut off by the
    // microphone opening a fraction of a second later, and a ring still sounding
    // over the first word of a conversation is the thing people apologise for.
    for (const osc of voices) {
      try { osc.stop(); } catch { /* already stopped on its own schedule */ }
    }
    try { context.close(); } catch { /* nothing left to close */ }
  };
}

/** Seconds left until a deadline, floored at zero. */
function secondsLeft(ringsUntil) {
  const until = Date.parse(ringsUntil || '');
  if (Number.isNaN(until)) return 0;
  return Math.max(0, Math.round((until - Date.now()) / 1000));
}

const BUTTON = [
  'flex:1',
  'padding:9px 12px',
  'border:0',
  'border-radius:8px',
  'font:600 13px/1.4 inherit',
  'cursor:pointer',
].join(';');

/**
 * Put the card on screen and ring until something happens to it.
 *
 * @param {object} opts
 * @param {object} opts.call  The `incomingCall` object off the update stream —
 *   `callId`, `from`, `project`, `reference`, `ringsUntil`, `recorded`.
 * @param {string} opts.accent  The integration's own colour, for Answer.
 * @param {string} [opts.position]  Same two words the toast takes.
 * @param {() => void} opts.onAnswer   Pressed Answer.
 * @param {() => void} opts.onDecline  Pressed Decline.
 * @param {() => void} [opts.onExpire] Nobody pressed anything in time.
 * @returns {(reason?: string) => void} Take it away. Called with no reason on
 *   an ordinary dismissal; the caller passes one only for its own logging.
 */
export function showIncomingCall({
  call, accent = '#5b6ef5', position = 'bottom-right',
  onAnswer, onDecline, onExpire,
}) {
  if (typeof document === 'undefined' || !document.body) return () => {};

  const [vertical, horizontal] = String(position).split('-');
  const el = document.createElement('div');
  el.setAttribute('role', 'alertdialog');
  el.setAttribute('aria-live', 'assertive');
  el.setAttribute('aria-label', `${call.from || 'Support'} is calling about ${call.reference}`);
  el.style.cssText = [
    'position:fixed',
    `${vertical === 'top' ? 'top' : 'bottom'}:20px`,
    `${horizontal === 'left' ? 'left' : 'right'}:20px`,
    // Above the update toast, which uses the maximum and may be on screen at
    // the same moment — a status line must not sit on top of a ringing phone.
    'z-index:2147483647',
    'width:300px',
    'max-width:calc(100vw - 40px)',
    'padding:14px',
    'border-radius:12px',
    'background:#1b1e26',
    'color:#eceef2',
    'font:13px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    'box-shadow:0 18px 44px rgba(0,0,0,0.5)',
    `border:1px solid ${accent}`,
    'opacity:0',
    'transform:translateY(8px)',
    'transition:opacity .2s ease,transform .2s ease',
  ].join(';');

  const heading = document.createElement('div');
  heading.style.cssText = 'font-weight:600;font-size:14px;margin-bottom:2px';
  // textContent throughout: every string on this card came from a person typing
  // it, and it is being written into somebody else's page.
  heading.textContent = `${call.from || 'Support'} is calling`;

  const about = document.createElement('div');
  about.style.cssText = 'color:#a4abba;margin-bottom:10px';
  about.textContent = call.project
    ? `About your report ${call.reference} · ${call.project}`
    : `About your report ${call.reference}`;

  const countdown = document.createElement('div');
  countdown.style.cssText = 'color:#7d8598;font-size:12px;margin-bottom:10px';

  const answer = document.createElement('button');
  answer.type = 'button';
  answer.textContent = 'Answer';
  answer.style.cssText = `${BUTTON};background:${accent};color:#fff`;

  const decline = document.createElement('button');
  decline.type = 'button';
  decline.textContent = 'Not now';
  decline.style.cssText = `${BUTTON};background:#2b2f3a;color:#c8cedb`;

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px';
  actions.append(answer, decline);

  el.append(heading, about, countdown);
  if (call.recorded) {
    const kept = document.createElement('div');
    kept.style.cssText = 'color:#7d8598;font-size:12px;margin-bottom:10px';
    kept.textContent = 'This call will be recorded.';
    el.append(kept);
  }
  el.append(actions);
  document.body.appendChild(el);

  const stopRinging = startRinging();
  let closed = false;
  let ticker = null;

  const close = () => {
    if (closed) return;
    closed = true;
    if (ticker) clearInterval(ticker);
    stopRinging();
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    setTimeout(() => el.remove(), 250);
  };

  const tick = () => {
    const left = secondsLeft(call.ringsUntil);
    countdown.textContent = left
      ? `Ringing — ${left}s`
      : 'The call has stopped ringing.';
    if (left) return;
    // The deadline is the server's, not this browser's arithmetic, for the
    // reason the API writes it down: two clocks disagreeing about when a phone
    // stopped ringing is how somebody gets cut off mid-answer.
    close();
    try { onExpire?.(); } catch { /* theirs */ }
  };
  tick();
  ticker = setInterval(tick, 1000);

  answer.addEventListener('click', () => {
    // Stopped before the handler runs, not after: opening a microphone takes a
    // moment, and a phone still ringing during it sounds like the button
    // did nothing.
    close();
    try { onAnswer?.(); } catch { /* theirs */ }
  });

  decline.addEventListener('click', () => {
    close();
    try { onDecline?.(); } catch { /* theirs */ }
  });

  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  });

  return close;
}
